import React, { createContext, useCallback, useMemo } from 'react'
import { GraphQL } from '@guestgoodie/graphql'
import { AuthClient, ReturnType, Session } from '@guestgoodie/auth-core'

import { Permission } from '../types'
import useSuspendedInit from '../hooks/useSuspendedInit'

import useSuspendedSession, {
  changePassword,
  client,
  refresh,
  resetPassword,
  signOut,
  switchOrg
} from '../hooks/useSuspendedSession'

type AuthContextType = {
  client: AuthClient
  isSignedIn: boolean
  hasOrg: boolean
  session: Session | null
  currentUser: GraphQL.User | null
  refresh: () => Promise<Session | null>
  switchOrg: (orgSlug: string) => Promise<ReturnType | null>
  signOut: () => void
  resetPassword: (email: string) => Promise<ReturnType | null>
  changePassword: (code: string, password: string) => Promise<void>
  hasPermission: (permission: Permission) => boolean
}

export const AuthContext = createContext<AuthContextType>({
  client,
  isSignedIn: false,
  hasOrg: false,
  session: null,
  currentUser: null,
  refresh: () => Promise.resolve(null),
  switchOrg: () => Promise.resolve(null),
  resetPassword: () => Promise.resolve(null),
  changePassword: () => Promise.resolve(),
  signOut: () => {},
  hasPermission: () => false
})

export const AuthProvider: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const session = useSuspendedSession()
  const { currentUser } = useSuspendedInit(session)

  const hasPermission = useCallback(
    (permission: string) => {
      return session?.permissions?.includes(permission) ?? false
    },
    [session?.permissions]
  )

  const value = useMemo(() => {
    return {
      client,
      isSignedIn: !!session,
      hasOrg: session?.currentOrgId != null,
      session,
      currentUser,
      resetPassword,
      changePassword,
      signOut,
      refresh,
      hasPermission,
      switchOrg
    }
  }, [session, currentUser, hasPermission])

  return <AuthContext value={value}>{children}</AuthContext>
}
